import { Banner } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks/src/use-resolution-info';
import { IGStoryMentionSlideoutBanner } from '@mm/icons/SlideoutBanners';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import { selectPromoterById } from '@mm/state/selectors/promoterSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSupportedWidgets } from '../../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { EmbeddableDialogueEditorWithPreview } from '../../../../features/startBotPromoters/common/StartBotsDialogueEditor/EmbeddableDialogueEditorWithPreview';
import { IG_SUPPORTED_WIDGET_TYPES } from '../../../../features/startBotPromoters/instagramStoryMention/InstagramStoryMentionEditor';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { FreeTrialWarning } from '../../elements/FreeTrialWarning';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './IGStoryMentionSlideoutEditor.module.css';

export interface IGStoryMentionSlideoutEditorProps {
  promoterId: number;
}

export const IGStoryMentionSlideoutEditor = ({
  promoterId,
}: IGStoryMentionSlideoutEditorProps) => {
  const promoter = useSelector(state => selectPromoterById(state, promoterId));
  const promoterCRUDActions = usePromoterCRUDActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const page = useSelector(state =>
    pageEntityByIdSelector(state, String(promoter?.pageId)),
  );
  const { screenWidth } = useResolutionInfo();
  const { setRef } = useSlideoutOnClose();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);

  useEffect(() => {
    async function fetchItems() {
      await promoterCRUDActions.retrieveAll(
        chatbotId,
        'instagram_story_mention',
      );
    }
    fetchItems();
  }, [chatbotId, promoterCRUDActions]);

  if (!promoter || !page) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader promoter={promoter} title="Instagram Story Mention" />

      <FreeTrialWarning />

      <SlideoutMenu.Content className={styles.content}>
        {!isMobile && (
          <Banner
            css={{
              collapsedHeader: styles.collapsedHeader,
              content: styles.bannerContent,
            }}
            withBannerHeader
            showCloseIcon
            usedInSlideout
            headerText="What is an Instagram Story Mention Autoresponder?"
            withLeftArea={false}
          >
            <IGStoryMentionSlideoutBanner textColor="white" />
          </Banner>
        )}
        <div className={styles.configArea}>
          <div className={styles.title}>
            Send this content instanly to users who mention you in their stories
          </div>
          <EmbeddableDialogueEditorWithPreview
            editPaneClassname={styles.dialogueEditPane}
            previewClassName={styles.dialogueEditPreviewPane}
            dialogueEditorClassName={styles.dialogueContainer}
            dialogueId={page.id}
            widgetsToExclude={widgetsToExclude}
            platforms={['instagram']}
            usedInSlideout
            hideDialogueSelector
            showAttributesStartWarning
            forInstagram
            withPreview
          />
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
